import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge, EmptyState } from '../components/ui.js';
import { Icon } from '../components/Icon.js';
import { formatMoney, toMinor, minorDivisor, type Currency } from '@chatrunner/schemas';

interface Zone {
  name: string;
  aliases: string[];
  fee_cents: number;
  currency: Currency;
  eta_hours: number | null;
}

const IRAQI_GOVERNORATES: Array<{ ar: string; en: string }> = [
  { ar: 'بغداد', en: 'Baghdad' },        { ar: 'البصرة', en: 'Basra' },
  { ar: 'نينوى', en: 'Nineveh' },       { ar: 'أربيل', en: 'Erbil' },
  { ar: 'السليمانية', en: 'Sulaymaniyah' }, { ar: 'دهوك', en: 'Duhok' },
  { ar: 'النجف', en: 'Najaf' },          { ar: 'كربلاء', en: 'Karbala' },
  { ar: 'واسط', en: 'Wasit' },           { ar: 'ديالى', en: 'Diyala' },
  { ar: 'الأنبار', en: 'Anbar' },        { ar: 'صلاح الدين', en: 'Salah al-Din' },
  { ar: 'بابل', en: 'Babil' },           { ar: 'القادسية', en: 'Qadisiyyah' },
  { ar: 'ذي قار', en: 'Dhi Qar' },       { ar: 'ميسان', en: 'Maysan' },
  { ar: 'المثنى', en: 'Muthanna' },      { ar: 'كركوك', en: 'Kirkuk' },
];

const CURRENCIES: Currency[] = ['IQD', 'USD', 'SAR', 'AED', 'EGP', 'JOD'];

export function DeliveryZones() {
  const api = useApi();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ['delivery-zones'],
    queryFn: () => api.get<{ zones: Zone[] }>('/knowledge/delivery-zones'),
  });

  const [zones, setZones] = useState<Zone[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<Currency>('IQD');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.zones) {
      setZones(data.zones);
      setDirty(false);
      if (data.zones[0]?.currency) setDefaultCurrency(data.zones[0].currency);
    }
  }, [data?.zones]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/knowledge/delivery-zones', {
        zones: zones.map((z) => ({
          name: z.name, aliases: z.aliases.filter(Boolean),
          fee_cents: z.fee_cents, currency: z.currency, eta_hours: z.eta_hours,
        })),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['delivery-zones'] }); setDirty(false); },
  });

  const existingNames = new Set(zones.map((z) => z.name.toLowerCase()));
  const presetsToShow = IRAQI_GOVERNORATES.filter(
    (g) => !existingNames.has(g.ar.toLowerCase()) && !existingNames.has(g.en.toLowerCase()),
  );

  function addZone(name: string, aliases: string[] = []) {
    setZones((arr) => [...arr, { name, aliases, fee_cents: 0, currency: defaultCurrency, eta_hours: null }]);
    setDirty(true);
  }
  function updateZone(idx: number, patch: Partial<Zone>) {
    setZones((arr) => arr.map((z, i) => (i === idx ? { ...z, ...patch } : z)));
    setDirty(true);
  }
  function removeZone(idx: number) {
    setZones((arr) => arr.filter((_, i) => i !== idx));
    setDirty(true);
  }

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.zones.title')}
        sub={t('page.zones.sub')}
        actions={
          <button className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            <Icon name="check" />{save.isPending ? t('common.saving') : dirty ? t('common.save') : t('common.saved')}
          </button>
        }
      />

      {isLoading ? (
        <div className="muted" style={{ padding: 24 }}>{t('common.loading')}</div>
      ) : zones.length === 0 ? (
        <EmptyState icon="truck" h={t('page.zones.empty.h')} p={t('page.zones.empty.p')} />
      ) : (
        <div className="grid2">
          {zones.map((z, i) => (
            <div className="card card--pad" key={i}>
              <div className="between" style={{ alignItems: 'flex-start' }}>
                <div className="flex aic gap8">
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, background: 'var(--accent-soft)',
                    color: 'var(--accent)', display: 'grid', placeItems: 'center',
                  }}><Icon name="pin" size={18} /></div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{z.name || t('page.zones.new')}</div>
                </div>
                <button onClick={() => removeZone(i)} className="iconbtn" style={{ color: 'var(--ink-3)' }} aria-label={t('common.delete')}>
                  <Icon name="trash" size={16} />
                </button>
              </div>

              <div className="field" style={{ marginTop: 14 }}>
                <label className="field__label">{t('page.zones.name')}</label>
                <input className="input" value={z.name} onChange={(e) => updateZone(i, { name: e.target.value })} placeholder={t('page.zones.name.ph')} />
              </div>

              <div className="field">
                <label className="field__label">{t('page.zones.aliases')}</label>
                <input
                  className="input"
                  value={z.aliases.join(', ')}
                  onChange={(e) => updateZone(i, { aliases: e.target.value.split(/[,،]/).map((s) => s.trim()).filter(Boolean) })}
                  placeholder={t('page.zones.aliases.ph')}
                />
              </div>

              <div className="grid2" style={{ gap: 12 }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field__label">{t('page.zones.fee')}</label>
                  <input
                    className="input mono"
                    inputMode="decimal"
                    value={(z.fee_cents / minorDivisor(z.currency)).toString()}
                    onChange={(e) => updateZone(i, { fee_cents: toMinor(Number(e.target.value) || 0, z.currency) })}
                  />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field__label">{t('page.zones.currency')}</label>
                  <select
                    className="selectbox"
                    value={z.currency}
                    onChange={(e) => updateZone(i, { currency: e.target.value as Currency })}
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="field" style={{ marginBottom: 0, marginTop: 12 }}>
                <label className="field__label">{t('page.zones.eta')}</label>
                <input
                  className="input mono"
                  inputMode="numeric"
                  value={z.eta_hours ?? ''}
                  placeholder={t('common.optional')}
                  onChange={(e) => updateZone(i, { eta_hours: e.target.value ? Number(e.target.value) : null })}
                />
              </div>

              <div className="flex aic gap8 wrapf" style={{ marginTop: 14 }}>
                <Badge kind="accent" plain icon="money">{formatMoney(z.fee_cents, z.currency)}</Badge>
                {z.eta_hours ? <Badge kind="ok" plain icon="clock">~{z.eta_hours}{lang === 'ar' ? ' ساعة' : 'h'}</Badge> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {presetsToShow.length > 0 && (
        <div>
          <h3 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 10 }}>{t('page.zones.quickAdd')}</h3>
          <div className="tmpls">
            {presetsToShow.map((g) => (
              <button key={g.en} type="button" className="tmpl" onClick={() => addZone(lang === 'ar' ? g.ar : g.en, [lang === 'ar' ? g.en : g.ar])}>
                <span style={{ fontWeight: 700 }}>+ {lang === 'ar' ? g.ar : g.en}</span>
                <span className="muted" style={{ fontSize: 11, marginInlineStart: 6 }}>{lang === 'ar' ? g.en : g.ar}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
